import { Portal } from '@radix-ui/react-tooltip';

import { useEffect, useState } from 'react';

import { markdownToComponent } from '@/utils/convertMarkdown';
import formatName from '@/utils/format/formatName';
import formatURL from '@/utils/format/formatURL';
import getCurrentIds from '@/utils/getCurrentIds';
import getSource from '@/utils/getSource';
import getUUID from '@/utils/getUUID';
import { cn } from '@/utils/lib/utils';
import { ArrowLeft, Globe } from '@/utils/lucide';
import { z_classes } from '@/utils/z';

import { SupportingQuote } from '@/types/Evidence';

import LoadIndicator from '../Loader';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface SupportingQuoteProps
{
	quote: SupportingQuote;
	index: number;
}

const SupportingQuoteComponent = ({ quote, index }: SupportingQuoteProps) =>
{
	const [quoteId, setQuoteId] = useState<string>(() => quote.id || getUUID());
	const [translatedText, setTranslatedText] = useState<string>('');
	const [showTranslation, setShowTranslation] = useState(false);
	const [detectedLanguage, setDetectedLanguage] = useState<string>('');
	const [isTranslating, setIsTranslating] = useState(false);

	// CJK detection regexes
	const japaneseRegex = /\p{Script=Hiragana}|\p{Script=Katakana}/u;
	const koreanRegex = /\p{Script=Hangul}/u;
	const chineseRegex = /\p{Script=Han}/u;

	// Detect CJK language
	const detectLanguage = (text: string): string =>
	{
		if (japaneseRegex.test(text)) return 'Japanese';
		if (koreanRegex.test(text)) return 'Korean';
		if (chineseRegex.test(text)) return 'Chinese';
		return '';
	};

	// Check if quote contains CJK characters
	const isCJK = quote.quote ? detectLanguage(quote.quote) !== '' : false;

	useEffect(() =>
	{
		if (isCJK && !detectedLanguage)
		{
			setDetectedLanguage(detectLanguage(quote.quote || ''));
		}
	}, [quote.quote, isCJK, detectedLanguage]);

	// Socket listener for translation response
	useEffect(() =>
	{
		const handleTranslation = (data: { quote_id: string; translated_text: string }) =>
		{
			if (data.quote_id === quoteId)
			{
				setTranslatedText(data.translated_text);
				setShowTranslation(true);
				setIsTranslating(false);
			}
		};

		socket.on('translate_quote', handleTranslation);

		return () =>
		{
			socket.off('translate_quote', handleTranslation);
		};
	}, [quoteId]);

	const handleTranslateClick = () =>
	{
		if (translatedText && !showTranslation)
		{
			// Show stored translation
			setShowTranslation(true);
		}
		else if (!translatedText)
		{
			// console.log('detectedLanguage', detectedLanguage, 'emitting for', quote);

			// Emit translation request
			setIsTranslating(true);
			socket.emit('translate_quote', {
				quote_id: quoteId,
				quote_text: quote.quote,
				detected_language: detectedLanguage,
				session_id: getCurrentIds().session_id
			});
		}
	};

	const handleShowOriginal = () =>
	{
		setShowTranslation(false);
	};

	if (!quote)
	{
		return null;
	}

	// If the quote is a compass search or already has quotes, don't add quotes
	const quoteText = quote.action_type === 'compass_search' || (quote.quote?.startsWith('"') && quote.quote?.endsWith('"')) ? quote.quote : `"${quote.quote}"`;

	const displayText = showTranslation && translatedText ? `"${translatedText}"` : quoteText;

	return (
		<Tooltip delayDuration={300} key={'quote' + index}>
			<TooltipTrigger asChild>
				<button
					key={'quote-button' + index}
					onClick={e => getSource(quote)}
					className='w-full text-left p-3 pl-2 hover:bg-gray-100 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 group cursor-pointer'
				>
					<div className='flex'>
						<div
							aria-hidden='true'
							className='min-w-0.5 max-w-0.5 bg-gray-300 group-hover:bg-gray-500 transition-colors mr-3 self-stretch rounded-full'
						/>
						<div className='flex-1'>
							<div className='flex items-start gap-2'>
								<p className='text-sm text-gray-700 group-hover:text-gray-900 transition-colors flex-1'>
									{markdownToComponent(displayText ?? '')}
								</p>
								{isCJK && (
									<div className='flex-shrink-0 mt-0.5'>
										{showTranslation ? (
											<Tooltip delayDuration={300}>
												<TooltipTrigger asChild>
													<button
														onClick={e =>
															{
															e.stopPropagation();
															handleShowOriginal();
														}}
														className='p-1 hover:bg-gray-200 rounded transition-colors'
													>
														<ArrowLeft className='h-3 w-3 text-gray-500' />
													</button>
												</TooltipTrigger>
												<TooltipContent>
													<p>Show original text</p>
												</TooltipContent>
											</Tooltip>
										) : isTranslating ? (
											<div className='p-1'>
												<LoadIndicator size='sm' />
											</div>
										) : (
											<Tooltip delayDuration={300}>
												<TooltipTrigger asChild>
													<button
														onClick={e =>
															{
															e.stopPropagation();
															handleTranslateClick();
														}}
														className='p-1 hover:bg-gray-200 rounded transition-colors min-w-4 min-h-4 flex items-center justify-center'
													>
														<Globe className='text-icon-sm text-gray-500' />
													</button>
												</TooltipTrigger>
												<TooltipContent>
													<p>This appears to be a {detectedLanguage} quote. Click to translate into English</p>
												</TooltipContent>
											</Tooltip>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</button>
			</TooltipTrigger>
			<Portal>
				<TooltipContent className={cn('md:max-w-[560px]', z_classes.overlay[2])} side='right'>
					<div className='flex flex-col gap-2 p-2 rounded-md'>
						<p className='text-gray-900 font-semibold'>{quote.title}</p>
						{quote.metadata ? (
							<>
								<p>
									{quote.metadata.map((meta, index) =>
										{
										return (
											<span key={index} className='text-gray-500'>
												{formatName(meta)}
												{index < quote.metadata!.length - 1 && <span> • </span>}
											</span>
										);
									})}
								</p>
							</>
						) : (
							<>
								<p>
									{!quote.url && quote.status_type && formatName(quote.status_type)}
									{quote.url && <span>{formatURL(quote.url)}</span>}
								</p>
							</>
						)}
						{quote.notes && <div className='max-h-[25dvh] overflow-y-auto bg-gray-50 p-2 rounded-md'>{markdownToComponent(quote.notes)}</div>}
					</div>
				</TooltipContent>
			</Portal>
		</Tooltip>
	);
};

export default SupportingQuoteComponent;
